import React, { useMemo } from 'react';
import * as yup from 'yup';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
    FIELD_LABELS,
    getStringFromDate,
    shortLabel,
    transformToNumber,
} from '../../../../../../helpers';
import { useUpsertOrderProductionMutation } from '../../../../../../services';
import {
    DSection,
    RhDateInput,
    RhFormWrapper,
    RHTextField,
} from '../../../../../dum';
import { BranchInput } from './branch/branch-input';
import { OrderProductionTypeInput } from './order-production-type/order-production-type-input';
import { OrderProductionProductsColumns } from './order-production-products/order-production-products-columns';
import { OrderProductionProductsConsumedColumns } from './order-production-products-consumed/order-production-products-consumed-columns';
import { OrderProductionEmployeesColumns } from './order-production-employees/order-production-employees-columns';
import { OrderProductionNamePrefixProvider } from './order-production-name-prefix.context';
import { IOrderProduction } from './order-production-form';
import { Box, Button, IconButton, Typography } from '@mui/material';

interface IBulkEntry {
    waste: number;
    order_production_products: IOrderProduction['order_production_products'];
    order_production_products_consumed: IOrderProduction['order_production_products_consumed'];
    order_production_employees: IOrderProduction['order_production_employees'];
}

interface IBulkOrderProduction {
    start_date: string;
    branch_id?: number | null;
    order_production_type_id?: number | null;
    shift?: number | null;
    entries: IBulkEntry[];
}

interface IBulkOrderProductionForm {
    onSuccess: () => void;
    disabled?: boolean;
}

const defaultEntry: IBulkEntry = {
    waste: 0,
    order_production_products: [
        { group_weight: 0, groups: 0, kilos: 0, machine: null, product: null, hours: 8 },
    ],
    order_production_products_consumed: [
        { group_weight: 0, groups: 0, kilos: 0, machine: null, product: null, hours: 8 },
    ],
    order_production_employees: [],
};

const entrySchema = yup.object({
    waste: yup
        .number()
        .transform(transformToNumber)
        .nullable()
        .required(`'Desperdicio' es obligatorio`),
    order_production_products: yup
        .array()
        .of(
            yup.object().shape({
                id: yup.number().notRequired().nullable(),
                machine: yup.mixed().required(`'Máquina' es obligatorio`).nullable(),
                product: yup.mixed().required(`'Producto' es obligatorio`).nullable(),
                kilos: yup.number().transform(transformToNumber).nullable().required(`'Kilos' es obligatorio`),
                groups: yup.number().transform(transformToNumber).nullable().required(`'Grupos' es obligatorio`),
                group_weight: yup.number().transform(transformToNumber).nullable().required(`'Peso del grupo' es obligatorio`),
                hours: yup.number().transform(transformToNumber).nullable().notRequired(),
            }),
        )
        .required(),
    order_production_products_consumed: yup
        .array()
        .of(
            yup.object().shape({
                id: yup.number().notRequired().nullable(),
                machine: yup.mixed().required(`'Máquina' es obligatorio`).nullable(),
                product: yup.mixed().required(`'Producto' es obligatorio`).nullable(),
                kilos: yup.number().transform(transformToNumber).nullable().required(`'Kilos' es obligatorio`),
                groups: yup.number().transform(transformToNumber).nullable().required(`'Grupos' es obligatorio`),
                group_weight: yup.number().transform(transformToNumber).nullable().required(`'Peso del grupo' es obligatorio`),
                hours: yup.number().transform(transformToNumber).nullable().notRequired(),
            }),
        )
        .required(),
    order_production_employees: yup
        .array()
        .of(
            yup.object().shape({
                employee: yup.mixed().required(`'Empleado' es obligatorio`).nullable(),
                is_leader: yup.mixed().notRequired(),
            }),
        )
        .required(),
});

const validationSchema: yup.SchemaOf<IBulkOrderProduction> = yup.object({
    start_date: yup.string().required(`'Fecha' es obligatorio`).nullable(),
    branch_id: yup.number().required(`'Sucursal' es obligatorio`).nullable(),
    order_production_type_id: yup
        .number()
        .required(`'Tipo de orden de producción' es obligatorio`)
        .nullable(),
    shift: yup
        .number()
        .transform(transformToNumber)
        .nullable()
        .required(`'Turno' es obligatorio`),
    entries: yup.array().of(entrySchema).min(1).required(),
});

function EntriesSection({ disabled }: { disabled: boolean }) {
    const { control } = useFormContext<IBulkOrderProduction>();
    const { fields, append, remove } = useFieldArray({ control, name: 'entries' });

    return (
        <>
            {fields.map((field, i) => (
                <OrderProductionNamePrefixProvider key={field.id} value={`entries.${i}.`}>
                    <DSection
                        title={`Producción ${i + 1}`}
                        action={
                            i > 0 ? (
                                <IconButton
                                    size={'small'}
                                    disabled={disabled}
                                    onClick={() => remove(i)}
                                    sx={{ mr: 2 }}
                                >
                                    <Typography variant={'body2'}>✕</Typography>
                                </IconButton>
                            ) : undefined
                        }
                    >
                        <RHTextField
                            name={`entries.${i}.waste`}
                            label={shortLabel(FIELD_LABELS.waste)}
                        />
                        <OrderProductionProductsColumns />
                        <OrderProductionProductsConsumedColumns />
                        <OrderProductionEmployeesColumns />
                    </DSection>
                </OrderProductionNamePrefixProvider>
            ))}
            <Box sx={{ mt: 1, mb: 2, px: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant={'outlined'}
                    disabled={disabled}
                    onClick={() => append(defaultEntry)}
                >
                    + Agregar producción
                </Button>
            </Box>
        </>
    );
}

export function BulkOrderProductionForm({
    onSuccess,
    disabled = false,
}: IBulkOrderProductionForm) {
    const [mutation, { loading }] = useUpsertOrderProductionMutation();

    const initialValues = useMemo<IBulkOrderProduction>(
        () => ({
            start_date: '',
            branch_id: null,
            order_production_type_id: null,
            shift: 0,
            entries: [{ ...defaultEntry }],
        }),
        [],
    );

    return (
        <RhFormWrapper
            validationSchema={validationSchema}
            initialValues={initialValues}
            disabled={loading || disabled}
            onSubmit={async (data) => {
                const { start_date, branch_id, order_production_type_id, shift, entries } = data;
                const startDateStr = getStringFromDate(start_date);
                const shiftValue = shift && shift > 0 ? shift : null;

                for (const entry of entries) {
                    await mutation({
                        variables: {
                            OrderProductionInput: {
                                id: null,
                                start_date: startDateStr,
                                branch_id: branch_id || null,
                                order_production_type_id: order_production_type_id || null,
                                waste: entry.waste ?? 0,
                                shift: shiftValue,
                                order_production_products: entry.order_production_products.map(
                                    (p) => ({
                                        // Always a create, but the key still
                                        // has to be explicit — the service
                                        // rejects an absent index property.
                                        id: null,
                                        kilos: p.kilos,
                                        product_id: p.product!.id,
                                        machine_id: p.machine!.id,
                                        group_weight: p.group_weight!,
                                        groups: p.groups || 0,
                                        hours: p.hours,
                                    }),
                                ),
                                order_production_products_consumed: entry.order_production_products_consumed.map(
                                    (r) => ({
                                        id: null,
                                        kilos: r.kilos,
                                        product_id: r.product!.id,
                                        machine_id: r.machine!.id,
                                        group_weight: r.group_weight!,
                                        groups: r.groups || 0,
                                        hours: r.hours,
                                    }),
                                ),
                                order_production_employees: entry.order_production_employees.map(
                                    (e) => ({
                                        employee_id: e.employee!.id,
                                        is_leader: e.is_leader ? 1 : 0,
                                    }),
                                ),
                            },
                        },
                    });
                }

                onSuccess();
            }}
            onInvalid={async (errors) => {
                console.error(errors);
            }}
        >
            <DSection alwaysOpen>
                <RhDateInput
                    name={'start_date'}
                    label={shortLabel(FIELD_LABELS.start_date)}
                />
                <BranchInput />
                <OrderProductionTypeInput />
                <RHTextField
                    name={'shift'}
                    label={shortLabel(FIELD_LABELS.shift)}
                />
            </DSection>
            <EntriesSection disabled={loading || disabled} />
        </RhFormWrapper>
    );
}
